// packages/utils/src/formatters.ts

const brlFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const dayMonthTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Sao_Paulo',
});

const relativeTimeFormatter = new Intl.RelativeTimeFormat('pt-BR', {
  numeric: 'auto',
  style: 'long',
});

/**
 * Formats a number as BRL currency.
 * @example formatCurrency(1500.50) -> "R$ 1.500,50"
 * @example formatCurrency("1500.50") -> "R$ 1.500,50"
 * @example formatCurrency(0) -> "R$ 0,00"
 * @example formatCurrency(null) -> "R$ 0,00"
 *
 * Fix UAT 2026-05-06: aceita string (DECIMAL do backend retorna string).
 * Antes só number → NaN em toda tela de detail/list quando backend mandava
 * "293.79" (DrizzleORM mapeia DECIMAL como string por design).
 */
export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return brlFormatter.format(0);
  }
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return brlFormatter.format(0);
  return brlFormatter.format(num);
}

/**
 * Formats an ISO 8601 string as a date (dd/MM/yyyy).
 * @example formatDate("2026-03-22T14:30:00Z") -> "22/03/2026"
 */
export function formatDate(isoString: string | null | undefined): string {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '-';
  return dateFormatter.format(date);
}

/**
 * Formats an ISO 8601 string as date + time.
 * @example formatDateTime("2026-03-22T14:30:00Z") -> "22/03/2026, 11:30"
 */
export function formatDateTime(isoString: string | null | undefined): string {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '-';
  return dateTimeFormatter.format(date);
}

/**
 * Formats an ISO 8601 string as short day/month + time in America/Sao_Paulo.
 * @example formatDayMonthTime("2026-07-13T14:25:00Z") -> "13/07 11:25"
 *
 * Returns '' for null/invalid inputs so callers can interpolate into
 * "· {{time}}" labels without leaving a dangling separator.
 */
export function formatDayMonthTime(isoString: string | null | undefined): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const parts = dayMonthTimeFormatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${pick('day')}/${pick('month')} ${pick('hour')}:${pick('minute')}`;
}

/**
 * Formats an ISO 8601 string as a relative time string in pt-BR.
 * @example formatRelativeTime("2026-03-22T14:30:00Z") -> "ha 2 horas"
 */
export function formatRelativeTime(isoString: string | null | undefined): string {
  if (!isoString) return '-';

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '-';
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);
  const diffWeek = Math.round(diffDay / 7);
  const diffMonth = Math.round(diffDay / 30);
  const diffYear = Math.round(diffDay / 365);

  if (Math.abs(diffSec) < 60) return relativeTimeFormatter.format(diffSec, 'second');
  if (Math.abs(diffMin) < 60) return relativeTimeFormatter.format(diffMin, 'minute');
  if (Math.abs(diffHour) < 24) return relativeTimeFormatter.format(diffHour, 'hour');
  if (Math.abs(diffDay) < 7) return relativeTimeFormatter.format(diffDay, 'day');
  if (Math.abs(diffWeek) < 5) return relativeTimeFormatter.format(diffWeek, 'week');
  if (Math.abs(diffMonth) < 12) return relativeTimeFormatter.format(diffMonth, 'month');
  return relativeTimeFormatter.format(diffYear, 'year');
}

/**
 * Formats a competence string (`YYYY-MM`) as a human-readable "month/year"
 * label in the given locale (month name via `Intl`, never hardcoded arrays).
 *
 * @example formatCompetence('2026-06') -> "junho/2026"
 * @example formatCompetence('2026-06', 'en-US') -> "June/2026"
 * @example formatCompetence(null) -> "-"
 *
 * Inputs that don't match `YYYY-MM` are returned unchanged so callers can
 * safely pipe raw backend values through this helper.
 */
export function formatCompetence(
  competence: string | null | undefined,
  locale: string = 'pt-BR',
): string {
  if (!competence) return '-';
  const match = /^(\d{4})-(\d{2})$/.exec(competence);
  if (!match) return competence;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || month < 1 || month > 12) return competence;
  const monthName = new Intl.DateTimeFormat(locale, { month: 'long' }).format(
    new Date(year, month - 1, 1),
  );
  return `${monthName}/${year}`;
}

/** Nó mínimo de uma cadeia de zonas para formatação de breadcrumb. */
export interface ZoneChainNode {
  level: string;
  name: string;
  code: string;
}

/** Deriva a UF (2 letras) do código de uma zona de estado (`BR-PR` → `PR`). */
function ufFromStateCode(code: string): string | null {
  const match = /^BR-([A-Z]{2})$/.exec(code);
  return match?.[1] ?? null;
}

/**
 * Breadcrumb legível de uma cadeia de zonas (ordenada root→leaf).
 *
 * Regras: estado vira UF abreviada; cidade vira nome; o leaf (quando
 * district/custom ou qualquer nível não já representado) vira nome — com
 * sufixo "(customizada)" se `level === 'custom'`. Níveis intermediários
 * (region / microregion / metropolitan_region) são omitidos para manter o
 * caminho curto e humano.
 *
 * @example formatZoneChain([{level:'region',...},{level:'state',code:'BR-PR',name:'Paraná'},{level:'microregion',...},{level:'city',name:'Curitiba'},{level:'district',name:'Centro'}]) -> "PR › Curitiba › Centro"
 * @example formatZoneChain([{level:'region',...},{level:'state',code:'BR-PR',name:'Paraná'}]) -> "PR"
 */
export function formatZoneChain(chain: ZoneChainNode[] | null | undefined): string {
  if (!chain || chain.length === 0) return '';
  const parts: string[] = [];

  const state = chain.find((z) => z.level === 'state');
  if (state) parts.push(ufFromStateCode(state.code) ?? state.name);

  const city = chain.find((z) => z.level === 'city');
  if (city) parts.push(city.name);

  const leaf = chain[chain.length - 1];
  if (leaf && leaf.level !== 'state' && leaf.level !== 'city' && leaf.level !== 'region') {
    parts.push(leaf.level === 'custom' ? `${leaf.name} (customizada)` : leaf.name);
  }

  if (parts.length === 0) return leaf?.name ?? '';
  return parts.join(' › ');
}
